import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from '../i18n';
import { ArrowLeft, ChevronRight } from 'lucide-react';

const sections = [
  { id: 'objet', label: '1. Objet' },
  { id: 'definitions', label: '2. Définitions' },
  { id: 'inscription', label: '3. Inscription' },
  { id: 'services', label: '4. Services' },
  { id: 'abonnements', label: '5. Abonnements' },
  { id: 'obligations', label: '6. Obligations' },
  { id: 'propriete', label: '7. Propriété intellectuelle' },
  { id: 'donnees', label: '8. Données personnelles' },
  { id: 'responsabilite', label: '9. Responsabilité' },
  { id: 'resiliation', label: '10. Résiliation' },
  { id: 'droit', label: '11. Droit applicable' },
];

const CGU = () => {
  const { t } = useTranslation();
  const [activeSection, setActiveSection] = useState('objet');

  useEffect(() => {
    const handleScroll = () => {
      const offsets = sections.map(({ id }) => {
        const el = document.getElementById(id);
        return { id, top: el ? el.getBoundingClientRect().top : Infinity };
      });
      const current = offsets.filter((o) => o.top <= 120).pop();
      if (current) setActiveSection(current.id);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-nexus-600 transition-colors mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour à l'accueil
          </Link>
          <h1 className="text-3xl font-bold text-slate-900">
            Conditions Générales d'Utilisation
          </h1>
          <p className="mt-2 text-sm text-slate-500">
            Dernière mise à jour : 17 avril 2026
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex gap-10">
          {/* Sidebar navigation (desktop only) */}
          <aside className="hidden lg:block w-64 flex-shrink-0">
            <nav className="sticky top-24 space-y-1">
              {sections.map(({ id, label }) => (
                <a
                  key={id}
                  href={`#${id}`}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                    activeSection === id
                      ? 'bg-nexus-50 text-nexus-700 font-medium'
                      : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  {activeSection === id && <ChevronRight className="w-3 h-3 flex-shrink-0" />}
                  <span>{label}</span>
                </a>
              ))}
            </nav>
          </aside>

          {/* Main content */}
          <div className="flex-1 min-w-0">
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 sm:p-8 lg:p-10">
              <div className="prose prose-slate max-w-none prose-headings:scroll-mt-24">
                <p className="text-slate-600 leading-relaxed">
                  Les présentes Conditions Générales d'Utilisation (ci-après « CGU ») régissent l'accès et
                  l'utilisation de la plateforme <strong>Nexus-360</strong> (ci-après la « Plateforme »),
                  éditée par la société <strong>ACL Gestion</strong>. En accédant à la Plateforme ou en
                  créant un compte, l'Utilisateur accepte sans réserve les présentes CGU.
                </p>

                {/* 1. Objet */}
                <h2 id="objet" className="text-xl font-bold text-slate-900 mt-10 mb-4">
                  1. Objet
                </h2>
                <p className="text-slate-600 leading-relaxed">
                  Les présentes CGU ont pour objet de définir les modalités et conditions dans lesquelles
                  la société ACL Gestion met à disposition la Plateforme Nexus-360, ainsi que les droits et
                  obligations des Utilisateurs dans le cadre de son utilisation. La Plateforme est un outil
                  de gestion en ligne de type SaaS (Software as a Service) destiné aux structures
                  professionnelles souhaitant centraliser la gestion de leur activité, de leurs
                  collaborateurs et de leur facturation.
                </p>

                {/* 2. Définitions */}
                <h2 id="definitions" className="text-xl font-bold text-slate-900 mt-10 mb-4">
                  2. Définitions
                </h2>
                <p className="text-slate-600 leading-relaxed mb-4">
                  Dans le cadre des présentes CGU, les termes suivants ont la signification qui leur est
                  attribuée ci-dessous :
                </p>
                <ul className="space-y-3 text-slate-600">
                  <li>
                    <strong>Plateforme :</strong> désigne le logiciel en ligne Nexus-360 accessible à
                    l'adresse <em>https://nexus-360.fr</em> et l'ensemble de ses fonctionnalités,
                    interfaces et services associés.
                  </li>
                  <li>
                    <strong>Utilisateur :</strong> désigne toute personne physique qui accède à la
                    Plateforme et/ou crée un compte, qu'elle agisse en son nom propre ou pour le compte
                    d'une personne morale.
                  </li>
                  <li>
                    <strong>Structure :</strong> désigne l'entité juridique (entreprise, association,
                    cabinet, etc.) au sein de laquelle l'Utilisateur exerce son activité et à laquelle
                    est rattaché un espace de gestion sur la Plateforme.
                  </li>
                  <li>
                    <strong>Abonnement :</strong> désigne la formule souscrite par la Structure pour
                    accéder aux fonctionnalités de la Plateforme, selon les modalités tarifaires en
                    vigueur.
                  </li>
                  <li>
                    <strong>Services :</strong> désigne l'ensemble des fonctionnalités, outils et modules
                    proposés par la Plateforme dans le cadre de l'Abonnement souscrit, incluant sans s'y
                    limiter la gestion des collaborateurs, la facturation, les abonnements et le catalogue
                    d'applications.
                  </li>
                </ul>

                {/* 3. Inscription et Création de Compte */}
                <h2 id="inscription" className="text-xl font-bold text-slate-900 mt-10 mb-4">
                  3. Inscription et Création de Compte
                </h2>
                <p className="text-slate-600 leading-relaxed">
                  L'accès aux Services de la Plateforme nécessite la création d'un compte Utilisateur.
                  Lors de l'inscription, l'Utilisateur s'engage à fournir des informations exactes,
                  complètes et à jour. L'Utilisateur est responsable de la confidentialité de ses
                  identifiants de connexion (adresse e-mail et mot de passe) et de toute activité
                  effectuée sous son compte.
                </p>
                <p className="text-slate-600 leading-relaxed mt-3">
                  En cas de soupçon d'utilisation non autorisée de son compte, l'Utilisateur doit en
                  informer immédiatement ACL Gestion à l'adresse{' '}
                  <a href="mailto:contact@nexus-360.fr" className="text-nexus-600 hover:underline">
                    contact@nexus-360.fr
                  </a>
                  . ACL Gestion se réserve le droit de suspendre ou de supprimer tout compte ne respectant
                  pas les présentes CGU, sans préavis ni indemnité.
                </p>
                <p className="text-slate-600 leading-relaxed mt-3">
                  L'Utilisateur peut être invité à rejoindre une Structure existante par un administrateur.
                  Dans ce cas, l'administrateur de la Structure est responsable de la gestion des droits
                  d'accès accordés à ses membres.
                </p>

                {/* 4. Description des Services */}
                <h2 id="services" className="text-xl font-bold text-slate-900 mt-10 mb-4">
                  4. Description des Services
                </h2>
                <p className="text-slate-600 leading-relaxed">
                  La Plateforme Nexus-360 propose un ensemble de services de gestion à destination des
                  structures professionnelles, incluant notamment :
                </p>
                <ul className="space-y-2 text-slate-600 mt-3">
                  <li>La gestion centralisée des structures et de leurs membres (utilisateurs, rôles, permissions).</li>
                  <li>La gestion des abonnements et des formules tarifaires.</li>
                  <li>Un module de facturation et de suivi comptable.</li>
                  <li>Un catalogue d'applications métier modulaires (comptabilité, SIRH, etc.).</li>
                  <li>Un tableau de bord avec indicateurs de performance et statistiques.</li>
                  <li>Un module de paramétrage et de personnalisation de l'espace de travail.</li>
                </ul>
                <p className="text-slate-600 leading-relaxed mt-3">
                  ACL Gestion se réserve le droit de faire évoluer, modifier ou supprimer tout ou partie
                  des Services proposés, sous réserve d'en informer les Utilisateurs dans un délai
                  raisonnable. Les évolutions majeures seront notifiées par e-mail ou par notification
                  au sein de la Plateforme.
                </p>

                {/* 5. Abonnements et Tarification */}
                <h2 id="abonnements" className="text-xl font-bold text-slate-900 mt-10 mb-4">
                  5. Abonnements et Tarification
                </h2>
                <p className="text-slate-600 leading-relaxed">
                  L'accès aux Services est soumis à la souscription d'un Abonnement payant. Les tarifs en
                  vigueur sont disponibles sur la page de tarification de la Plateforme et peuvent être
                  modifiés à tout moment par ACL Gestion, sous réserve d'un préavis de trente (30) jours.
                </p>
                <p className="text-slate-600 leading-relaxed mt-3">
                  <strong>Période d'essai :</strong> chaque nouvelle Structure bénéficie d'une période
                  d'essai gratuite de sept (7) jours à compter de la date de création de son compte. Durant
                  cette période, l'ensemble des fonctionnalités de la Plateforme sont accessibles. À l'issue
                  de la période d'essai, la Structure devra souscrire un Abonnement pour continuer à
                  utiliser les Services.
                </p>
                <p className="text-slate-600 leading-relaxed mt-3">
                  <strong>Facturation :</strong> les Abonnements sont facturés sur une base mensuelle ou
                  annuelle, selon le choix de la Structure. La facturation annuelle ouvre droit à une
                  remise par rapport à la facturation mensuelle. Le paiement est effectué par carte
                  bancaire via le prestataire de paiement sécurisé Stripe. Les factures sont disponibles
                  dans l'espace de gestion de la Structure.
                </p>
                <p className="text-slate-600 leading-relaxed mt-3">
                  <strong>Renouvellement :</strong> sauf résiliation intervenant avant la date d'échéance,
                  l'Abonnement est automatiquement renouvelé pour une durée identique à la période initiale
                  choisie. L'Utilisateur peut résilier son Abonnement à tout moment depuis son espace de
                  gestion ; la résiliation prendra effet à la fin de la période en cours.
                </p>

                {/* 6. Obligations de l'Utilisateur */}
                <h2 id="obligations" className="text-xl font-bold text-slate-900 mt-10 mb-4">
                  6. Obligations de l'Utilisateur
                </h2>
                <p className="text-slate-600 leading-relaxed">
                  L'Utilisateur s'engage à :
                </p>
                <ul className="space-y-2 text-slate-600 mt-3">
                  <li>Utiliser la Plateforme conformément à sa destination et dans le respect des lois et réglementations applicables.</li>
                  <li>Fournir des informations exactes et à jour lors de son inscription et tout au long de l'utilisation des Services.</li>
                  <li>Assurer la confidentialité de ses identifiants de connexion et ne pas les communiquer à des tiers.</li>
                  <li>Ne pas tenter d'accéder de manière non autorisée aux systèmes, réseaux ou données de la Plateforme.</li>
                  <li>Ne pas utiliser la Plateforme à des fins illicites, frauduleuses ou portant atteinte aux droits de tiers.</li>
                  <li>Ne pas reproduire, copier, vendre ou exploiter tout ou partie de la Plateforme sans l'autorisation écrite préalable d'ACL Gestion.</li>
                  <li>Signaler sans délai toute faille de sécurité ou utilisation non autorisée de son compte.</li>
                </ul>

                {/* 7. Propriété Intellectuelle */}
                <h2 id="propriete" className="text-xl font-bold text-slate-900 mt-10 mb-4">
                  7. Propriété Intellectuelle
                </h2>
                <p className="text-slate-600 leading-relaxed">
                  L'ensemble des éléments composant la Plateforme Nexus-360 — incluant sans limitation le
                  code source, les logiciels, les bases de données, les interfaces, les textes, les
                  graphismes, les images, les logos, les marques, les sons et les vidéos — est la propriété
                  exclusive d'ACL Gestion ou de ses partenaires, et est protégé par les dispositions du
                  Code de la propriété intellectuelle et par les traités internationaux applicables.
                </p>
                <p className="text-slate-600 leading-relaxed mt-3">
                  L'Abonnement confère à l'Utilisateur un droit d'utilisation personnel, non exclusif, non
                  cessible et non transférable de la Plateforme, pour la durée de l'Abonnement et dans les
                  limites prévues par les présentes CGU. Toute reproduction, représentation, modification,
                  publication, adaptation ou exploitation non autorisée de tout ou partie de la Plateforme
                  constitue une contrefaçon sanctionnée par les articles L.335-2 et suivants du Code de la
                  propriété intellectuelle.
                </p>
                <p className="text-slate-600 leading-relaxed mt-3">
                  Les données saisies par l'Utilisateur sur la Plateforme restent sa propriété. L'Utilisateur
                  concède à ACL Gestion un droit d'utilisation limité de ces données aux seules fins de
                  fourniture et d'amélioration des Services.
                </p>

                {/* 8. Données Personnelles */}
                <h2 id="donnees" className="text-xl font-bold text-slate-900 mt-10 mb-4">
                  8. Données Personnelles
                </h2>
                <p className="text-slate-600 leading-relaxed">
                  ACL Gestion accorde une importance particulière à la protection des données personnelles
                  de ses Utilisateurs. Les informations relatives à la collecte, au traitement et à la
                  protection des données personnelles sont détaillées dans notre{' '}
                  <Link to="/confidentialite" className="text-nexus-600 hover:underline font-medium">
                    Politique de Confidentialité
                  </Link>
                  , qui fait partie intégrante des présentes CGU.
                </p>
                <p className="text-slate-600 leading-relaxed mt-3">
                  En utilisant la Plateforme, l'Utilisateur reconnaît avoir pris connaissance de la
                  Politique de Confidentialité et accepte les traitements de données personnelles qui y
                  sont décrits. Les traitements sont effectués conformément au Règlement Général sur la
                  Protection des Données (RGPD) et à la loi n° 78-17 du 6 janvier 1978 relative à
                  l'informatique, aux fichiers et aux libertés.
                </p>

                {/* 9. Responsabilité et Garanties */}
                <h2 id="responsabilite" className="text-xl font-bold text-slate-900 mt-10 mb-4">
                  9. Responsabilité et Garanties
                </h2>
                <p className="text-slate-600 leading-relaxed">
                  ACL Gestion s'engage à mettre en œuvre tous les moyens raisonnables pour assurer la
                  disponibilité et le bon fonctionnement de la Plateforme. Toutefois, la Plateforme est
                  fournie « en l'état » et ACL Gestion ne garantit pas que les Services seront exempts
                  d'erreurs, de bugs ou d'interruptions.
                </p>
                <p className="text-slate-600 leading-relaxed mt-3">
                  ACL Gestion ne saurait être tenue responsable :
                </p>
                <ul className="space-y-2 text-slate-600 mt-3">
                  <li>Des dommages directs ou indirects résultant de l'utilisation ou de l'impossibilité d'utiliser la Plateforme.</li>
                  <li>De la perte de données résultant d'une défaillance technique, sauf en cas de faute prouvée d'ACL Gestion.</li>
                  <li>Des interruptions de service dues à des opérations de maintenance, des mises à jour ou des cas de force majeure.</li>
                  <li>Du contenu saisi par les Utilisateurs sur la Plateforme.</li>
                  <li>Des dommages résultant d'une utilisation non conforme de la Plateforme par l'Utilisateur.</li>
                </ul>
                <p className="text-slate-600 leading-relaxed mt-3">
                  En tout état de cause, la responsabilité d'ACL Gestion, toutes causes confondues, est
                  limitée au montant des sommes effectivement versées par la Structure au titre de
                  l'Abonnement au cours des douze (12) derniers mois précédant l'événement générateur
                  de responsabilité.
                </p>

                {/* 10. Résiliation */}
                <h2 id="resiliation" className="text-xl font-bold text-slate-900 mt-10 mb-4">
                  10. Résiliation
                </h2>
                <p className="text-slate-600 leading-relaxed">
                  L'Utilisateur peut résilier son Abonnement à tout moment depuis son espace de gestion sur
                  la Plateforme. La résiliation prend effet à l'expiration de la période d'Abonnement en
                  cours. Aucun remboursement au prorata ne sera effectué pour la période restante.
                </p>
                <p className="text-slate-600 leading-relaxed mt-3">
                  ACL Gestion se réserve le droit de suspendre ou de résilier l'accès d'un Utilisateur à la
                  Plateforme, sans préavis ni indemnité, en cas de :
                </p>
                <ul className="space-y-2 text-slate-600 mt-3">
                  <li>Manquement aux obligations prévues par les présentes CGU.</li>
                  <li>Utilisation frauduleuse ou abusive de la Plateforme.</li>
                  <li>Non-paiement de l'Abonnement après relance restée sans effet pendant quinze (15) jours.</li>
                  <li>Comportement portant atteinte à la sécurité ou au bon fonctionnement de la Plateforme.</li>
                </ul>
                <p className="text-slate-600 leading-relaxed mt-3">
                  En cas de résiliation, l'Utilisateur peut demander l'export de ses données dans un format
                  standard (CSV, JSON) dans un délai de trente (30) jours suivant la résiliation. Passé ce
                  délai, ACL Gestion se réserve le droit de supprimer définitivement les données associées
                  au compte.
                </p>

                {/* 11. Droit Applicable et Litiges */}
                <h2 id="droit" className="text-xl font-bold text-slate-900 mt-10 mb-4">
                  11. Droit Applicable et Litiges
                </h2>
                <p className="text-slate-600 leading-relaxed">
                  Les présentes CGU sont régies par le droit français. Tout litige relatif à
                  l'interprétation, l'exécution ou la résiliation des présentes CGU sera soumis à la
                  compétence exclusive des tribunaux compétents de Paris, sauf disposition impérative
                  contraire.
                </p>
                <p className="text-slate-600 leading-relaxed mt-3">
                  Préalablement à toute action judiciaire, les parties s'engagent à rechercher une solution
                  amiable dans un délai de trente (30) jours à compter de la notification du litige par
                  lettre recommandée avec accusé de réception. À défaut de résolution amiable dans ce
                  délai, le litige sera porté devant les juridictions compétentes.
                </p>
                <p className="text-slate-600 leading-relaxed mt-3">
                  Conformément aux articles L.616-1 et R.616-1 du Code de la consommation, en cas de
                  litige non résolu, le consommateur peut recourir gratuitement au service de médiation.
                  Le médiateur de la consommation compétent est accessible via la plateforme européenne de
                  résolution des litiges en ligne :{' '}
                  <a
                    href="https://ec.europa.eu/consumers/odr"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-nexus-600 hover:underline"
                  >
                    https://ec.europa.eu/consumers/odr
                  </a>
                  .
                </p>

                {/* Contact */}
                <div className="mt-10 p-6 bg-slate-50 rounded-xl border border-slate-200">
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">Contact</h3>
                  <p className="text-slate-600">
                    Pour toute question relative aux présentes CGU, vous pouvez contacter ACL Gestion à
                    l'adresse suivante :{' '}
                    <a href="mailto:contact@nexus-360.fr" className="text-nexus-600 hover:underline font-medium">
                      contact@nexus-360.fr
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CGU;
